import { useEffect, useState } from "react";
import styled from "styled-components";
import { queryClient } from "../..";
import { useAppSelector } from "../../app/hooks";
import { companyState } from "../../app/reducers/company-slice";
import {
  CompanyWithCollaborativesQueryKey,
  useGetCompanyWithCollaborativesPageQuery,
} from "../../query/company-query";
import { useDebouncedCallback } from "../../utils/custom-hook/use-debounced-callback";
import useDidMountEffect from "../../utils/custom-hook/use-did-mount-effect";
import { ContainedYellowButton } from "../button/ContainedYellowButton";
import { PageController } from "../paging/PageController";
import { BasicSingleSelector } from "../selector/BasicSingleSelector";
import { CompanyWithCollaborativesListTable } from "../table/CompanyWithCollaborativesListTable";
import { IconTextField } from "../text-field/IconTextField";
import EventBus, {EventTypes} from "../../utils/eventbus/event-bus";

interface Props {
  handleClickDialogOpen: () => void;
  handleClickDialogClose: () => void;
}

const searchTabMap: { [key: string]: string } = {
  name: "이름",
  phone: "전화번호",
};

const placeholderMap: { [key: string]: string } = {
  name: "회사 이름을 입력하세요",
  phone: "전화번호를 입력하세요",
};

export const CompanyPanel: React.FC<Props> = ({
  handleClickDialogOpen,
  handleClickDialogClose,
}) => {
  const { selectedCompanyWithCollaboratives, companyWithCollaborativesPage } =
    useAppSelector(companyState);
  const [payload, setPayload] = useState<any>({});
  const [searchKeyword, setSearchKeyword] = useState<string>("");
  const [selectedSearchTabStatus, setSelectedSearchTabStatus] =
    useState<string>("name");
  useGetCompanyWithCollaborativesPageQuery(payload);

  const setSearchPayload = (page: number = 0) => {
    const newPayload: any = {
      page,
    };
    if (selectedSearchTabStatus.length > 0 && searchKeyword !== "") {
      newPayload[selectedSearchTabStatus] = searchKeyword;
    }
    setPayload(newPayload);
    queryClient.invalidateQueries(CompanyWithCollaborativesQueryKey.Page);
  };

  const debouncedSearchPayload = useDebouncedCallback(
    async (page: number = 0) => setSearchPayload(page)
  );

  const handleClickSearchLeftTabItem = (key: string) => {
    setSelectedSearchTabStatus(key);
  };

  const handleChangeTextField = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newSearchKeyword = event.target.value;
    setSearchKeyword(newSearchKeyword);
  };

  const handleChangePage = (selectedItem: { selected: number }) => {
    const page = selectedItem.selected;
    setSearchPayload(page);
  };

  useDidMountEffect(() => {
    debouncedSearchPayload();
    EventBus.emit(EventTypes.PAGE_CLEAR);
  }, [searchKeyword]);

  useDidMountEffect(() => {
    setSearchKeyword("");
  }, [selectedSearchTabStatus]);

  useDidMountEffect(() => {
    if (selectedCompanyWithCollaboratives) {
      handleClickDialogOpen();
    } else {
      handleClickDialogClose();
    }
  }, [selectedCompanyWithCollaboratives]);

  useEffect(() => {
    setSearchPayload();
  }, []);

  return (
    <>
      <CompanyTabPanelContainer>
        <CompanyTabPanelTitle>
          <PanelTitleLeftContainer>
            <SelectorContainer>
              <BasicSingleSelector
                value={searchTabMap[selectedSearchTabStatus]}
                defaultKey={"이름"}
                candidateMap={searchTabMap}
                onClickSelectItem={handleClickSearchLeftTabItem}
                width="7.063rem"
              />
            </SelectorContainer>
            <IconTextField
              placeholder={placeholderMap[selectedSearchTabStatus]}
              name={selectedSearchTabStatus}
              value={searchKeyword}
              onChange={handleChangeTextField}
              width="19.438rem"
            />
          </PanelTitleLeftContainer>
          <PanelTitleRightContainer>
            <ContainedYellowButton
              width="8.188rem"
              height="3.188rem"
              text={"신규 등록"}
              onClick={handleClickDialogOpen}
            />
          </PanelTitleRightContainer>
        </CompanyTabPanelTitle>
        <CompanyTabPanelContent>
          <CompanyTableContainer>
            <CompanyWithCollaborativesListTable />
            <PageController
              basePage={companyWithCollaborativesPage}
              onPageChange={handleChangePage}
            />
          </CompanyTableContainer>
        </CompanyTabPanelContent>
      </CompanyTabPanelContainer>
    </>
  );
};

const CompanyTabPanelContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const CompanyTabPanelTitle = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  top: -64px;
  right: 0px;
`;

const PanelTitleLeftContainer = styled.div`
  display: flex;
`;

const PanelTitleRightContainer = styled.div``;

const CompanyTabPanelContent = styled.div`
  padding-top: 1.25rem;
  width: 100%;
  height: 100%;
`;

const CompanyTableContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const SelectorContainer = styled.div`
  margin-right: 0.5rem;
`;
